import React, { useCallback, useMemo } from 'react';

import clsx from 'clsx';
import * as O from 'fp-ts/Option';
import { useAtom, useAtomValue } from 'jotai';

import { SideMenuContent } from './SideMenuContent';
import { SideMobileMenuContent } from './SideMobileMenuContent';

import { sideMenuForceOpen } from '../../model/state/sideMenuState';
import { userState } from '../../model/state/userState';

interface SideMenuProps {
  children: JSX.Element;
}

export const SideMenu: React.FC<SideMenuProps> = ({ children }) => {
  const user = useAtomValue(userState);
  const isLoggedIn = useMemo(() => O.isSome(user), [user]);

  const [forceShowMenu, setForceShowMenu] = useAtom(sideMenuForceOpen);
  const showMenu = isLoggedIn && forceShowMenu;

  const hideSideMenu = useCallback(() => {
    if (!forceShowMenu) return;

    setForceShowMenu(false);
  }, [forceShowMenu, setForceShowMenu]);

  return (
    <div className="flex flex-row lg:min-w-[1024px]">
      {isLoggedIn && (
        <>
          <div
            className={clsx(
              'hidden flex-shrink-0 bg-white transition-all duration-1000 lg:block',
              showMenu ? 'w-[200px] min-w-[200px]' : 'w-[58px] min-w-[58px]'
            )}
          >
            <div
              className={clsx(
                'sticky left-0 top-0 h-screen transition-all duration-1000',
                showMenu ? 'w-[200px]' : 'w-[58px]'
              )}
            >
              <SideMenuContent showMenu={showMenu} />
            </div>
          </div>
          {forceShowMenu && (
            <div
              className={`fixed bottom-0 left-0 right-0 top-0 z-10 flex h-full w-full bg-[rgba(0,0,0,0.2)] lg:hidden`}
              onClick={hideSideMenu}
            />
          )}
          <div
            className={clsx(
              'absolute z-20 flex-shrink-0 bg-white transition-all duration-500 lg:hidden',
              showMenu ? 'w-[65%] max-w-[250px]' : 'w-0 '
            )}
          >
            <div
              className={clsx(
                'sticky left-0 top-0 h-screen transition-all duration-500',
                showMenu ? 'w-[65%]' : 'w-0'
              )}
            >
              <SideMobileMenuContent />
            </div>
          </div>
        </>
      )}

      <div className="h-full w-full overflow-y-auto lg:h-auto lg:min-h-screen">{children}</div>
    </div>
  );
};
